<template>
  <div
    class="position-relative audio-icon"
    :class="{ 'has-recording': audioUrl, 'no-recording': !audioUrl }"
  >
    <span
      id="microphone"
      :class="{ pointer: !audioUrl, 'no-recording': !audioUrl }"
    >
      <div class="top">
        <i class="fal fa-microphone top-icon microphone-icon"></i>
      </div>
      <div class="bottom" :style="`height: ${progress}%;`">
        <i class="fal fa-microphone bottom-icon microphone-icon"></i>
      </div>
    </span>
    <div
      v-if="audioUrl && !deletable"
      class="locked-icon"
      v-tooltip="{
        content: $t('BIO.LABEL.AUDIO_IS_LOCKED'),
        delay: 100,
        placement: 'top',
        container: 'body',
      }"
    >
      <i class="far fa-lock-alt c-white"></i>
    </div>
    <HMPopover target="microphone" placement="bottomleft">
      <HMPopoverMenuItem v-if="audioUrl" @click="$emit('toggleLockAudio')">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <span class="mr-5">{{
            deletable
              ? $t('BIO.LABEL.LOCK_AUDIO')
              : $t('BIO.LABEL.UNLOCK_AUDIO')
          }}</span>
          <i class="far fa-lock-alt c-coral"></i>
        </div>
      </HMPopoverMenuItem>
      <HMPopoverMenuItem v-if="audioUrl" @click="$emit('removeAudio')">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <span class="mr-5">{{ $t('BIO.LABEL.REMOVE_AUDIO') }}</span>
          <i class="far fa-trash-alt c-coral"></i>
        </div>
      </HMPopoverMenuItem>
      <HMPopoverMenuItem v-if="!audioUrl" @click="$emit('uploadAudio')">
        {{ $t('BIO.LABEL.UPLOAD_AUDIO') }}
      </HMPopoverMenuItem>
      <HMPopoverMenuItem v-if="!audioUrl" @click="$emit('recordAudio')">
        {{ $t('BIO.LABEL.RECORD_MESSAGE') }}
      </HMPopoverMenuItem>
    </HMPopover>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DepletingAudioIcon',
  props: {
    bio: {
      type: Object,
    },
  },
  computed: {
    audioUrl() {
      return this.bio?.audio?.storage_url;
    },
    deletable() {
      return this.bio?.audio?.deletable;
    },
    progress() {
      if (!this.audioUrl) return 0;
      if (!this.deletable) return 100;
      return 100 - Math.floor((this.audioMinutesAge * 100) / 1440);
    },
    audioMinutesAge() {
      if (!this.bio?.audio?.uploaded) return 0;
      return moment().diff(moment(this.bio.audio.uploaded), 'minutes');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

$mic_icon_size_mobile: 44px;
$mic_icon_size: 76px;
$close_size: 26px;

.audio-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  width: $mic_icon_size_mobile + 4px;
  height: $mic_icon_size_mobile + 4px;

  &.has-recording {
    background: var(--brand-color, $coral);
    background: linear-gradient(
      50deg,
      white 0%,
      var(--brand-color, $coral) 70%,
      var(--brand-color, $coral) 100%
    );
  }

  &.no-recording {
    width: $mic_icon_size_mobile + 2px;
    height: $mic_icon_size_mobile + 2px;
    border: 2px solid $coral;
  }

  @include media-breakpoint-up(md) {
    width: $mic_icon_size + 6px;
    height: $mic_icon_size + 6px;

    &.no-recording {
      width: $mic_icon_size + 2px;
      height: $mic_icon_size + 2px;
      border: 2px solid $coral;
    }
  }
}

#microphone {
  background-color: white;
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  padding: 1rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 30px;

  border: 6px solid white;
  width: $mic_icon_size_mobile;
  height: $mic_icon_size_mobile;

  @include media-breakpoint-up(md) {
    width: $mic_icon_size;
    height: $mic_icon_size;
  }

  &.no-recording {
    width: $mic_icon_size_mobile - 2px;
    height: $mic_icon_size_mobile - 2px;
  }

  @include media-breakpoint-up(md) {
    &.no-recording {
      width: $mic_icon_size - 2px;
      height: $mic_icon_size - 2px;
    }
  }
}

.microphone-icon {
  font-size: 16px;
  @include media-breakpoint-up(md) {
    font-size: 30px;
  }
}

.locked-icon {
  width: $close_size;
  height: $close_size;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $charcoal;
  color: white;
  border-radius: 50%;
  position: absolute;
  cursor: pointer;
  z-index: 2;
  border: 2px solid white;
  font-size: 12px;
}

.top {
  position: absolute;
  margin: 0 auto;
  background-color: white;
  color: $coral;
  width: 100px;
  height: 100%;
  z-index: 1;
}

.bottom {
  position: absolute;
  left: 0;
  bottom: 0;
  background-color: $coral;
  color: white;
  width: 100%;
  height: 100%;
  z-index: 2;
  overflow: hidden;
  transition: all 0.1s linear;
}

.top-icon {
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
  text-align: center;
  @include media-breakpoint-up(md) {
    top: 19px;
  }
}

.bottom-icon {
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  text-align: center;
  @include media-breakpoint-up(md) {
    bottom: 15px;
  }
}
</style>
